import React, { useRef } from "react";
import ReactToPrint from "react-to-print";
import PrintHeader from "./PrintHeader"; // Import PrintHeader

const TAB_LABELS = {
  // SENCO tab eventKeys
  "overview": "Class Results",
  "performance": "Class Analysis",
  "recommendations": "Insights",
  "settings": "Planning SET",
  "results3": "Individual Child",
  "results4": "Class Average",
  "progress": "Progress",
  // Regular results page tab eventKeys
  "My Class": "My Class",
  "Class Analysis": "Class Analysis",
  "Class Insights": "Insights",
  "Planning SET": "Planning SET",
  "Individual Child": "Individual Child",
  "Class Average": "Class Average",
  // Shared
  "Not Screened": "Screening Status",
};

const replaceCanvasWithImages = (sourceEl, cloneEl) => {

  const sourceCanvases = sourceEl.querySelectorAll("canvas");
  const cloneCanvases = cloneEl.querySelectorAll("canvas");

  sourceCanvases.forEach((sourceCanvas, i) => {
    const cloneCanvas = cloneCanvases[i];

    if (!cloneCanvas) return;

    const img = document.createElement("img");
    img.src = sourceCanvas.toDataURL();
    img.width = sourceCanvas.width;
    img.height = sourceCanvas.height;
    img.style.cssText = sourceCanvas.style.cssText;
    
    cloneCanvas.parentNode.replaceChild(img, cloneCanvas);
  });
};

const PrintActionCardSenco = ({
  cardPosition,
  handleCloseCard,
  activeAccordionRef,
  activeAccordionKey,
  performanceBodyRef,
  recommendationsBodyRef,
  planningSetBodyRef,
  allStudentsBodyRef,
  allStudentsAccordionKey,
  individualChildBodyRef,
  classAvgComparisonBodyRef,
  classAvgClassBodyRef,
  activeClassAverageSubTab,
  screeningPointBodyRef,
  classroomProgressBodyRef,
  activeProgressSubTab,
  // Regular results page
  classAnalysisBodyRef,
  classInsightsBodyRef,
  classInsightsSubtestsBodyRef,
  classInsightsPerformanceBodyRef,
  activeInsightsAccordionKey,
  classAverageBodyRef,
  screeningStatusBodyRef,
  classDetails,
  className,
  academicYear,
  assessmentName,
  activeTab
}) => {
  const printCardRef = useRef(null);
  const printContainerRef = useRef(null);
  const contentDivRef = useRef(null);
  const currentTabLabel = TAB_LABELS[activeTab];

  // Resolve display name — SENCO passes className, regular page passes classDetails
  const resolvedClassName = className || classDetails?.name || "";

  const getHeaderClassName = () => {
    if (activeTab === "results4" && activeClassAverageSubTab === "classAverageComparison") 
    {
      return "All Classes";
    }
    if (activeTab === "progress" && activeProgressSubTab === "classroomProgress") 
    {
      return "All Classes";
    }
    if (activeTab === "settings" && !activeAccordionKey) 
    {
      return "All Classes";
    }
    return resolvedClassName;
  };

  const getContent = () => {
    const sourceElements = [];

    if (activeTab === "performance" && performanceBodyRef?.current) 
    {
      sourceElements.push(performanceBodyRef.current);
    } 
    else if (activeTab === "recommendations" && recommendationsBodyRef?.current) 
    {
      sourceElements.push(recommendationsBodyRef.current);
    } 
    else if (activeTab === "settings") 
    {
      if (allStudentsAccordionKey === "all-students" && allStudentsBodyRef?.current) 
      {
        sourceElements.push(allStudentsBodyRef.current);
      }
      if (activeAccordionKey && planningSetBodyRef?.current) 
      {
        sourceElements.push(planningSetBodyRef.current);
      }
    } 
    else if ((activeTab === "results3" || activeTab === "Individual Child") && individualChildBodyRef?.current)
    {
      sourceElements.push(individualChildBodyRef.current);
    } 
    else if (activeTab === "results4") 
    {
      if (activeClassAverageSubTab === "classAverageComparison" && classAvgComparisonBodyRef?.current) 
      {
        sourceElements.push(classAvgComparisonBodyRef.current);
      } 
      else if (activeClassAverageSubTab === "classAverage" && classAvgClassBodyRef?.current) 
      {
        sourceElements.push(classAvgClassBodyRef.current);
      }
    } 
    else if (activeTab === "progress") 
    {
      if (activeProgressSubTab === "screeningProgressComparison" && screeningPointBodyRef?.current) 
      {
        sourceElements.push(screeningPointBodyRef.current);
      } 
      else if (activeProgressSubTab === "classroomProgress" && classroomProgressBodyRef?.current) 
      {
        sourceElements.push(classroomProgressBodyRef.current);
      }
    } 
    else if (activeTab === "Planning SET" && planningSetBodyRef?.current)
    {
      sourceElements.push(planningSetBodyRef.current);
    }
    else if (activeTab === "Class Average" && classAverageBodyRef?.current)
    {
      sourceElements.push(classAverageBodyRef.current);
    }
    else if (activeTab === "Not Screened" && screeningStatusBodyRef?.current)
    {
      sourceElements.push(screeningStatusBodyRef.current);
    }
    else if (activeTab === "Class Analysis" && classAnalysisBodyRef?.current) 
    {
      sourceElements.push(classAnalysisBodyRef.current);
    }
    else if (activeTab === "Class Insights") 
    {
      if (activeInsightsAccordionKey === "0" && classInsightsBodyRef?.current) 
      {
        sourceElements.push(classInsightsBodyRef.current);
      } 
      else if (activeInsightsAccordionKey === "1" && classInsightsSubtestsBodyRef?.current) 
      {
        sourceElements.push(classInsightsSubtestsBodyRef.current);
      } 
      else if (activeInsightsAccordionKey === "2" && classInsightsPerformanceBodyRef?.current) 
      {
        sourceElements.push(classInsightsPerformanceBodyRef.current);
      }
    }
    else if (activeAccordionRef?.current) 
    {
      sourceElements.push(activeAccordionRef.current);
    }

    if (sourceElements.length > 0 && contentDivRef.current) 
    {
      contentDivRef.current.innerHTML = "";
      sourceElements.forEach((sourceEl) => {
        if (!sourceEl || typeof sourceEl.cloneNode !== "function") return;
        const clone = sourceEl.cloneNode(true);
        replaceCanvasWithImages(sourceEl, clone);
        clone.querySelectorAll(".alpaca-print-header").forEach((el) => el.remove());
        contentDivRef.current.appendChild(clone);
      });
    }

    return printContainerRef.current;
  };

  return (
    <>
      <div
        ref={printCardRef}
        className="action-card"
        style={{
          position: "absolute",
          top: `${cardPosition.top}px`,
          left: `${cardPosition.left}px`,
          zIndex: 1000,
          width: "180px",
          padding: "16px", // Ensure consistent padding
        }}
      >
        <div className="action-card-header" style={{ marginBottom: "16px" }}>
          <p className="action-header">Print</p>
          <button onClick={handleCloseCard} className="close-button">
            ✕
          </button>
        </div>
        <div className="action-card-body">
          <ReactToPrint
            trigger={() => (
              <button
                className="blue-action-button"
                style={{ marginBottom: "16px" }}
              >
                Current View
              </button>
            )}
            content={getContent}
            onBeforePrint={() => console.log("[Print] Current View - Active Tab:", currentTabLabel)}
          />
        </div>
      </div>
      {/* Hidden print container */}
      <div style={{ display: "none" }}>
        <div ref={printContainerRef}>
          <PrintHeader className={getHeaderClassName()} academicYear={academicYear} assessmentName={assessmentName} />
          <div ref={contentDivRef} />
        </div>
      </div>
    </>
  );
};

export default PrintActionCardSenco;
